import { queryOptions } from '@tanstack/react-query'
import type { Database } from '@/types/supabase'
import { FunctionsHttpError } from '@supabase/supabase-js'
import { type Features, parseFeatures } from '@/lib/features'
import { supabase } from '@/lib/supabase/client'
import type { OrganizationRole } from './organization-memberships'

export type ActivityBand = 'active' | 'moderate' | 'low' | 'inactive' | 'never'

/**
 * One row of a credits-by-product-area breakdown.
 *
 * `action_code` is credit_ledger.action_code — a usage_types code for a spend
 * ('image', 'vto', 'upscale', 'video', 'iris.chat') or a credit.* code. It is
 * deliberately a plain string rather than a union: the ledger's action_code has
 * no foreign key so that a row survives a usage type being renamed or retired,
 * and a client-side union would start throwing on exactly the historical rows
 * that design protects.
 *
 * `label` is resolved server-side from usage_types, falling back to the code, so
 * the admin dashboard and the customer usage page name things identically.
 */
export interface UsageTypeCount {
  action_code: string
  label: string
  credits: number
  /**
   * Second level for the admin drill-down: `metadata.generation_type` stamped on
   * every generation charge, grouped. Analytics, not pricing — the price list
   * has one row per price, and this is how the drawer still shows model vs
   * try-on inside "Images". Absent on rows that carry no kind (Iris).
   */
  kinds?: { kind: string; credits: number }[]
}

/**
 * Map a "last active" timestamp to a CSM-friendly activity band.
 * Boundaries match the bands Bonnie defined: 7 / 14 / 30 days.
 */
export function deriveActivityBand(
  lastActiveAt: string | null | undefined,
  now: Date = new Date()
): ActivityBand {
  if (!lastActiveAt) {
    return 'never'
  }
  const ts = new Date(lastActiveAt).getTime()
  if (Number.isNaN(ts)) {
    return 'never'
  }
  const ageMs = now.getTime() - ts
  const day = 24 * 60 * 60 * 1000
  if (ageMs <= 7 * day) {
    return 'active'
  }
  if (ageMs <= 14 * day) {
    return 'moderate'
  }
  if (ageMs <= 30 * day) {
    return 'low'
  }
  return 'inactive'
}

/**
 * Admin organization with computed counts.
 * Uses direct SDK with PostgREST aggregates instead of RPC.
 */
export interface AdminOrganization {
  id: string
  name: string
  created_at: string
  deactivated_at: string | null
  billing_account_id: string
  billing_account_name: string | null
  member_count: number
  workspace_count: number
}

export type AdminBillingAccount =
  Database['public']['Functions']['get_admin_billing_accounts']['Returns'][number]

/**
 * Per-org last-active timestamp (super admin only).
 *
 * Single grouped query against credit_ledger — used to drive the Activity
 * column on /admin/organizations without fanning out a query per row.
 */
export const adminOrgsLastActiveQueryOptions = () =>
  queryOptions({
    queryKey: ['admin', 'organizations', 'last-active'],
    queryFn: async (): Promise<Record<string, string>> => {
      const { data, error } = await supabase.rpc('get_admin_orgs_last_active')

      if (error) {
        throw new Error(
          `Failed to fetch org last-active timestamps: ${error.message}`,
          { cause: error }
        )
      }

      const map: Record<string, string> = {}
      for (const row of data ?? []) {
        if (row.org_id && row.last_active_at) {
          map[row.org_id] = row.last_active_at
        }
      }
      return map
    },
    staleTime: 5 * 60 * 1000,
  })

/**
 * Query options for fetching all organizations metadata (super admin only).
 *
 * Uses direct Supabase SDK with PostgREST aggregate functions:
 * - RLS policy grants super admins SELECT access to all organizations
 * - PostgREST count() aggregates compute member/workspace counts server-side
 * - Single efficient query with no RPC overhead
 *
 * Security: RLS policy checks is_super_admin() for SELECT access.
 */
export const adminOrganizationsQueryOptions = () =>
  queryOptions({
    queryKey: ['admin', 'organizations'],
    queryFn: async (): Promise<AdminOrganization[]> => {
      const { data, error } = await supabase
        .from('organizations')
        .select(
          `
          id, name, created_at, deactivated_at, billing_account_id,
          billing_accounts(name),
          organization_memberships(count()),
          workspaces(count())
        `
        )
        .order('deactivated_at', { ascending: true, nullsFirst: true })
        .order('name', { ascending: true })

      if (error) {
        throw new Error(`Failed to fetch organizations: ${error.message}`, {
          cause: error,
        })
      }

      // Transform nested PostgREST response to flat structure
      return (data || []).map((org) => ({
        id: org.id,
        name: org.name,
        created_at: org.created_at,
        deactivated_at: org.deactivated_at,
        billing_account_id: org.billing_account_id,
        billing_account_name: org.billing_accounts?.name ?? null,
        member_count: org.organization_memberships?.[0]?.count ?? 0,
        workspace_count: org.workspaces?.[0]?.count ?? 0,
      }))
    },
    // Cache for 5 minutes (admin dashboards don't need real-time data)
    staleTime: 5 * 60 * 1000,
  })

/**
 * Query options for fetching all billing accounts metadata (super admin only).
 *
 * This query calls the get_admin_billing_accounts() PostgreSQL function which:
 * - Verifies super admin status SERVER-SIDE (cannot be bypassed)
 * - Returns metadata for ALL billing accounts (RLS bypassed via SECURITY DEFINER)
 * - Automatically logs access to super_admin_audit_logs
 * - Contains billing account names, soft limits, org counts, usage totals
 *
 * Security: Uses RPC call which enforces server-side validation.
 */
export const adminBillingAccountsQueryOptions = () =>
  queryOptions({
    queryKey: ['admin', 'billing-accounts'],
    queryFn: async (): Promise<AdminBillingAccount[]> => {
      const { data, error } = await supabase.rpc('get_admin_billing_accounts')

      if (error) {
        throw new Error(`Failed to fetch billing accounts: ${error.message}`, {
          cause: error,
        })
      }

      return data || []
    },
    // Cache for 5 minutes
    staleTime: 5 * 60 * 1000,
  })

export type AdminDashboardMetrics =
  Database['public']['Functions']['get_admin_dashboard_metrics']['Returns'][number]

export interface DailyCredits {
  date: string
  credits: number
}

export interface DailyUsers {
  date: string
  total_users: number
  mau: number
}

export interface DateRange {
  from: Date
  to: Date
}

/**
 * Query options for fetching dashboard metrics (super admin only).
 *
 * This query calls the get_admin_dashboard_metrics() PostgreSQL function which:
 * - Verifies super admin status SERVER-SIDE (cannot be bypassed)
 * - Returns key business metrics (total users, MAU, generations)
 * - Returns daily generation data for charting
 * - Accepts optional date range for filtering
 * - Automatically logs access to super_admin_audit_logs
 *
 * Security: Uses RPC call which enforces server-side validation.
 */
export const adminDashboardMetricsQueryOptions = (dateRange?: DateRange) =>
  queryOptions({
    queryKey: [
      'admin',
      'dashboard-metrics',
      dateRange?.from.toISOString().split('T')[0],
      dateRange?.to.toISOString().split('T')[0],
    ],
    queryFn: async (): Promise<AdminDashboardMetrics> => {
      const { data, error } = await supabase.rpc(
        'get_admin_dashboard_metrics',
        {
          p_start_date: dateRange?.from.toISOString().split('T')[0],
          p_end_date: dateRange?.to.toISOString().split('T')[0],
        }
      )

      if (error) {
        throw new Error(`Failed to fetch dashboard metrics: ${error.message}`, {
          cause: error,
        })
      }

      // RPC returns an array with single row
      if (!data || data.length === 0) {
        throw new Error('No dashboard metrics returned')
      }

      return data[0]
    },
    // Cache for 2 minutes (dashboard should be relatively fresh)
    staleTime: 2 * 60 * 1000,
  })

/**
 * Period preset used by the org detail drawer. "all" is encoded as a wide
 * range starting from the unix epoch so the same RPC handles every case.
 */
export type OrgDetailPeriod = '7d' | '30d' | '90d' | 'all'

export const ORG_DETAIL_PERIODS: { value: OrgDetailPeriod; label: string }[] = [
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: '90d', label: '90 days' },
  { value: 'all', label: 'All time' },
]

export function periodToDateRange(period: OrgDetailPeriod): {
  from: string
  to: string
} {
  const now = new Date()
  const to = now.toISOString().split('T')[0]
  if (period === 'all') {
    return { from: '1970-01-01', to }
  }
  const days = period === '7d' ? 7 : period === '30d' ? 30 : 90
  const fromDate = new Date(now)
  fromDate.setDate(fromDate.getDate() - (days - 1))
  return { from: fromDate.toISOString().split('T')[0], to }
}

export interface AdminOrgMember {
  user_id: string
  email: string
  full_name: string | null
  avatar_url: string | null
  role: OrganizationRole
  joined_at: string
  last_active_at: string | null
  credits_total: number
  credits_by_type: UsageTypeCount[]
}

export interface AdminOrgDetail {
  last_active_at: string | null
  credits_total: number
  credits_by_type: UsageTypeCount[]
  product_count: number
  asset_count: number
  workspace_count: number
  active_workspace_count: number
  members: AdminOrgMember[]
}

/**
 * Org health/adoption payload used by the super admin org detail drawer.
 *
 * Calls get_admin_org_detail() which:
 * - Verifies super admin status SERVER-SIDE
 * - Returns inventory + per-type usage + per-user breakdown in one round-trip
 *
 * Inventory counts (products/assets/workspaces) are point-in-time current
 * state. Usage counts are scoped to the period.
 */
export const adminOrgDetailQueryOptions = (
  orgId: string,
  period: OrgDetailPeriod
) =>
  queryOptions({
    queryKey: ['admin', 'organizations', orgId, 'detail', period],
    queryFn: async (): Promise<AdminOrgDetail> => {
      if (!orgId || orgId.trim() === '') {
        throw new Error(
          'Invalid orgId: cannot fetch org detail without valid organization ID'
        )
      }

      const { from, to } = periodToDateRange(period)
      const { data, error } = await supabase.rpc('get_admin_org_detail', {
        p_org_id: orgId,
        p_start_date: from,
        p_end_date: to,
      })

      if (error) {
        throw new Error(`Failed to fetch org detail: ${error.message}`, {
          cause: error,
        })
      }

      if (!data || data.length === 0) {
        throw new Error('No org detail returned')
      }

      const row = data[0]
      return {
        last_active_at: row.last_active_at ?? null,
        credits_total: row.credits_total ?? 0,
        credits_by_type:
          (row.credits_by_type as unknown as UsageTypeCount[]) ?? [],
        product_count: row.product_count ?? 0,
        asset_count: row.asset_count ?? 0,
        workspace_count: row.workspace_count ?? 0,
        active_workspace_count: row.active_workspace_count ?? 0,
        members: (row.members as unknown as AdminOrgMember[]) ?? [],
      }
    },
    staleTime: 2 * 60 * 1000,
  })

export interface AdminBillingOrgRow {
  id: string
  name: string
  deactivated_at: string | null
  created_at: string
  member_count: number
  last_active_at: string | null
  period_credits: number
}

export interface AdminBillingTrendPoint {
  period_index: number
  period_start: string
  period_end: string
  period_label: string
  credits: number
}

export interface AdminBillingAccountDetail {
  /**
   * The billing-period credit block, identical to what the customer's own usage
   * page shows for this account — the RPC returns get_usage verbatim so the two
   * views cannot disagree.
   */
  plan_code: string | null
  plan_name: string | null
  credit_limit: number
  credits_used: number
  allowance_remaining: number
  wallet_balance: number
  wallet_granted: number
  wallet_spent: number
  remaining: number
  period_start: string
  period_end: string
  is_trial: boolean
  org_count: number
  active_org_count: number
  /**
   * Credits in the user-selected window (the 7d/30d/90d toggle), which is a
   * different question from the billing period above and will usually disagree
   * with credits_used.
   */
  period_credits: number
  period_active_users: number
  usage_trend: AdminBillingTrendPoint[]
  orgs: AdminBillingOrgRow[]
}

/**
 * Contract-level health payload for the super admin billing account drawer.
 *
 * Different concerns from the org drawer: rollup metrics across all orgs
 * funded by this billing account, plus per-period trend so renewal /
 * upsell conversations are data-driven. Per-user / per-feature breakdowns
 * are deliberately not here — those live on the org drawer.
 */
export const adminBillingAccountDetailQueryOptions = (
  billingAccountId: string,
  period: OrgDetailPeriod
) =>
  queryOptions({
    queryKey: ['admin', 'billing-accounts', billingAccountId, 'detail', period],
    queryFn: async (): Promise<AdminBillingAccountDetail> => {
      if (!billingAccountId || billingAccountId.trim() === '') {
        throw new Error(
          'Invalid billingAccountId: cannot fetch detail without valid id'
        )
      }

      const { from, to } = periodToDateRange(period)
      const { data, error } = await supabase.rpc(
        'get_admin_billing_account_detail',
        {
          p_billing_account_id: billingAccountId,
          p_start_date: from,
          p_end_date: to,
        }
      )

      if (error) {
        throw new Error(
          `Failed to fetch billing account detail: ${error.message}`,
          { cause: error }
        )
      }

      if (!data || data.length === 0) {
        throw new Error('No billing account detail returned')
      }

      const row = data[0]
      return {
        plan_code: row.plan_code ?? null,
        plan_name: row.plan_name ?? null,
        credit_limit: row.credit_limit ?? 0,
        credits_used: row.credits_used ?? 0,
        allowance_remaining: row.allowance_remaining ?? 0,
        wallet_balance: row.wallet_balance ?? 0,
        wallet_granted: row.wallet_granted ?? 0,
        wallet_spent: row.wallet_spent ?? 0,
        remaining: row.remaining ?? 0,
        period_start: row.period_start,
        period_end: row.period_end,
        is_trial: row.is_trial ?? false,
        org_count: row.org_count ?? 0,
        active_org_count: row.active_org_count ?? 0,
        period_credits: row.period_credits ?? 0,
        period_active_users: row.period_active_users ?? 0,
        usage_trend:
          (row.usage_trend as unknown as AdminBillingTrendPoint[]) ?? [],
        orgs: (row.orgs as unknown as AdminBillingOrgRow[]) ?? [],
      }
    },
    staleTime: 2 * 60 * 1000,
  })

/**
 * Request body for creating an organization
 */
export interface CreateOrganizationRequest {
  org_name: string
  add_super_admin_as_member: boolean
  billing_account_id: string
}

/**
 * Response from creating an organization
 */
export interface CreateOrganizationResponse {
  org_id: string
}

/**
 * Creates a new organization linked to an existing billing account (super admin only).
 *
 * Uses RPC which atomically:
 * - Verifies super admin status SERVER-SIDE
 * - Creates organization linked to billing account (FK validates billing account)
 * - Optionally adds creator as org_admin
 *
 * @throws {Error} If validation fails, not authorized, or creation fails
 */
export async function createOrganization(
  input: CreateOrganizationRequest
): Promise<CreateOrganizationResponse> {
  const { data, error } = await supabase.rpc('admin_create_organization', {
    p_org_name: input.org_name,
    p_billing_account_id: input.billing_account_id,
    p_add_creator_as_admin: input.add_super_admin_as_member,
  })

  if (error) {
    throw error // Pass through for unique constraint handling
  }

  return { org_id: data }
}

export type GrantReason =
  'credit.contract' | 'credit.goodwill' | 'credit.refund'

export const GRANT_REASONS: {
  value: GrantReason
  label: string
  hint: string
}[] = [
  {
    value: 'credit.contract',
    label: 'Contract',
    hint: 'Credits under a signed contract or renewal',
  },
  {
    value: 'credit.goodwill',
    label: 'Goodwill',
    hint: 'A gesture — something went wrong, or a thank-you',
  },
  {
    value: 'credit.refund',
    label: 'Refund',
    hint: 'Giving back credits that were charged in error',
  },
]

/**
 * Grants credits to a billing account's wallet (super admin only). They never
 * expire and are drawn on after the monthly allowance — on a plan with no
 * allowance, the wallet is the whole balance. Idempotent when a ref is supplied; otherwise the RPC
 * mints one, so a double-click submits twice — which is why the dialog
 * disables its button while pending.
 */
export async function grantCredits(input: {
  billing_account_id: string
  credits: number
  reason: GrantReason
  note?: string
}): Promise<string> {
  const { data, error } = await supabase.rpc('grant_credits', {
    p_billing_account_id: input.billing_account_id,
    p_credits: input.credits,
    p_action_code: input.reason,
    p_note: input.note ?? undefined,
  })
  if (error) {
    throw new Error(`Failed to grant credits: ${error.message}`, {
      cause: error,
    })
  }
  return data as string
}

export interface AdminPlan {
  code: string
  name: string
  description: string | null
  is_managed: boolean
  monthly_credits: number
  features: Features
  stripe_product_id: string | null
  stripe_price_id_monthly: string | null
  stripe_price_id_yearly: string | null
  monthly_price_cents: number | null
  yearly_price_cents: number | null
  currency: string
}

/** The plan catalogue — readable by anyone; the tier ladder is public data. */
export const plansQueryOptions = () =>
  queryOptions({
    queryKey: ['plans'],
    queryFn: async (): Promise<AdminPlan[]> => {
      const { data, error } = await supabase
        .from('plans')
        .select(
          'code, name, description, is_managed, monthly_credits, features, stripe_product_id, stripe_price_id_monthly, stripe_price_id_yearly, monthly_price_cents, yearly_price_cents, currency'
        )
        .order('monthly_credits')
      if (error) {
        throw new Error(`Failed to fetch plans: ${error.message}`, {
          cause: error,
        })
      }
      // Managed (Enterprise) first: it is the default for anything created by
      // hand in admin, since that is by definition a sales-led deal.
      return (data ?? [])
        .map((row) => ({ ...row, features: parseFeatures(row.features) }))
        .sort((a, b) => Number(b.is_managed) - Number(a.is_managed))
    },
    staleTime: 10 * 60 * 1000,
  })

/**
 * Creates a self-serve billing account (super admin only): on Free, with its
 * welcome credits, in one transaction. From here the customer upgrades
 * through Stripe; admin only grants credits.
 */
export async function createSelfServeAccount(name: string): Promise<string> {
  const { data, error } = await supabase.rpc('create_self_serve_account', {
    p_name: name,
  })
  if (error) {
    throw error // Pass through for unique constraint handling
  }
  return data as string
}

export type UsageUnit = 'item' | 'second' | 'token'

export interface AdminUsageType {
  code: string
  label: string
  credits: number
  unit: UsageUnit
  unit_size: number
  is_active: boolean
}

/** The price list — one row per usage type; world-readable. */
export const usageTypesQueryOptions = () =>
  queryOptions({
    queryKey: ['usage-types'],
    queryFn: async (): Promise<AdminUsageType[]> => {
      const { data, error } = await supabase
        .from('usage_types')
        .select('code, label, credits, unit, unit_size, is_active')
        .order('code')
      if (error) {
        throw new Error(`Failed to fetch usage types: ${error.message}`, {
          cause: error,
        })
      }
      return (data ?? []) as AdminUsageType[]
    },
    staleTime: 10 * 60 * 1000,
  })

export interface ModelPrice {
  model: string
  usage_type: 'image' | 'video'
  label: string
  credits: number
  provider_cost_cents: number | null
  is_active: boolean
}

/**
 * The price list by model — whole credits per unit of the usage type (per
 * image, per second of video). World-readable: the pickers show what a model
 * costs before the user commits. A model with no row costs the type's default.
 */
export const modelPricesQueryOptions = () =>
  queryOptions({
    queryKey: ['model-prices'],
    queryFn: async (): Promise<ModelPrice[]> => {
      const { data, error } = await supabase
        .from('model_prices')
        .select(
          'model, usage_type, label, credits, provider_cost_cents, is_active'
        )
        .order('usage_type')
        .order('credits')
        .order('model')
      if (error) {
        throw new Error(`Failed to fetch model prices: ${error.message}`, {
          cause: error,
        })
      }
      return (data ?? []) as ModelPrice[]
    },
    staleTime: 10 * 60 * 1000,
  })

/** Edits a model's price (super admin only). Applies to future charges only. */
export async function updateModelPrice(input: {
  model: string
  label?: string
  credits?: number
  provider_cost_cents?: number | null
  is_active?: boolean
}): Promise<void> {
  const { model, ...patch } = input
  const { error } = await supabase
    .from('model_prices')
    .update(patch)
    .eq('model', model)
  if (error) {
    throw new Error(`Failed to update model price: ${error.message}`, {
      cause: error,
    })
  }
}

/** Prices a model that has no row yet (super admin only). */
export async function createModelPrice(input: {
  model: string
  usage_type: 'image' | 'video'
  label: string
  credits: number
  provider_cost_cents: number | null
}): Promise<void> {
  const { error } = await supabase.from('model_prices').insert(input)
  if (error) {
    throw new Error(`Failed to add model price: ${error.message}`, {
      cause: error,
    })
  }
}

/**
 * Edits a price (super admin only). Applies to future charges only: every
 * ledger row carries the credits it was charged at the time. For metered
 * types, unit_size is how many tokens make one billable unit; the meter reads
 * it at write time and carries its remainder, so a change lands on the next
 * crossing with nothing recomputed.
 */
export async function updateUsageType(input: {
  code: string
  label?: string
  credits?: number
  unit_size?: number
  is_active?: boolean
}): Promise<void> {
  const { code, ...patch } = input
  const { error } = await supabase
    .from('usage_types')
    .update(patch)
    .eq('code', code)
  if (error) {
    throw new Error(`Failed to update price: ${error.message}`, {
      cause: error,
    })
  }
}

/**
 * Puts a billing account on a plan (super admin only). Creates the
 * subscription if there is none, otherwise switches it in place — the period
 * anchor is kept, so a plan change never restarts a month.
 */
export async function setBillingAccountPlan(input: {
  billing_account_id: string
  plan_code: string
}): Promise<void> {
  const { error } = await supabase.rpc('set_billing_account_plan', {
    p_billing_account_id: input.billing_account_id,
    p_plan_code: input.plan_code,
  })
  if (error) {
    throw new Error(`Failed to set plan: ${error.message}`, { cause: error })
  }
}

export interface CreateBillingAccountRequest {
  name: string
  /** Every account has a plan from the start; admin-created ones default to Enterprise. */
  plan_code: string
  /**
   * Wins over the plan's monthly_credits. Null means "use the plan", which is
   * the normal case — this is how Enterprise and grandfathered accounts get a
   * bespoke allowance without a plan row each.
   */
  credit_allowance_override?: number | null
  is_trial?: boolean
  /** Per-account feature overrides merged over the plan; null = plan as-is. */
  features_override?: Features | null
}

/**
 * Response from creating a billing account
 */
export interface CreateBillingAccountResponse {
  id: string
}

/**
 * Creates a new billing account (super admin only).
 *
 * Uses direct SDK access via RLS policy that grants super admins INSERT access.
 *
 * @throws {Error} If not authorized or creation fails
 */
export async function createBillingAccount(
  input: CreateBillingAccountRequest
): Promise<CreateBillingAccountResponse> {
  const { data, error } = await supabase
    .from('billing_accounts')
    .insert({
      name: input.name,
      credit_allowance_override: input.credit_allowance_override ?? null,
      is_trial: input.is_trial ?? false,
      features_override: input.features_override ?? null,
    })
    .select('id')
    .single()

  if (error) {
    throw new Error(`Failed to create billing account: ${error.message}`, {
      cause: error,
    })
  }

  await setBillingAccountPlan({
    billing_account_id: data.id,
    plan_code: input.plan_code,
  })

  return { id: data.id }
}

/**
 * Request body for updating a billing account
 */
export interface UpdateBillingAccountRequest {
  billing_account_id: string
  name?: string
  credit_allowance_override?: number | null
  is_trial?: boolean
  /** Per-account feature overrides merged over the plan; null = plan as-is. */
  features_override?: Features | null
}

/**
 * Response from updating a billing account
 */
export interface UpdateBillingAccountResponse {
  success: boolean
}

/**
 * Updates an existing billing account (super admin only).
 *
 * Uses direct SDK access via RLS policy that grants super admins UPDATE access.
 *
 * @throws {Error} If not authorized or update fails
 */
export async function updateBillingAccount(
  input: UpdateBillingAccountRequest
): Promise<UpdateBillingAccountResponse> {
  // Build update object with only provided fields
  const updateData: {
    name?: string
    credit_allowance_override?: number | null
    is_trial?: boolean
    features_override?: Features | null
  } = {}
  if (input.name !== undefined) {
    updateData.name = input.name
  }
  if (input.credit_allowance_override !== undefined) {
    updateData.credit_allowance_override = input.credit_allowance_override
  }
  if (input.is_trial !== undefined) {
    updateData.is_trial = input.is_trial
  }
  if (input.features_override !== undefined) {
    updateData.features_override = input.features_override
  }

  if (Object.keys(updateData).length === 0) {
    throw new Error('No fields to update')
  }

  const { error } = await supabase
    .from('billing_accounts')
    .update(updateData)
    .eq('id', input.billing_account_id)

  if (error) {
    throw new Error(`Failed to update billing account: ${error.message}`, {
      cause: error,
    })
  }

  return { success: true }
}

/**
 * Request body for deactivating an organization
 */
export interface DeactivateOrganizationRequest {
  org_id: string
}

/**
 * Response from deactivating an organization
 */
export interface DeactivateOrganizationResponse {
  success: boolean
}

/**
 * Deactivates an organization (super admin only).
 *
 * @throws {Error} If not authorized or deactivation fails
 */
export async function deactivateOrganization(
  input: DeactivateOrganizationRequest
): Promise<DeactivateOrganizationResponse> {
  const { error } = await supabase.rpc('admin_set_organization_deactivated', {
    p_org_id: input.org_id,
    p_deactivated: true,
  })

  if (error) {
    throw new Error(`Failed to deactivate organization: ${error.message}`, {
      cause: error,
    })
  }

  return { success: true }
}

/**
 * Request body for reactivating an organization
 */
export interface ReactivateOrganizationRequest {
  org_id: string
}

/**
 * Response from reactivating an organization
 */
export interface ReactivateOrganizationResponse {
  success: boolean
}

/**
 * Reactivates a deactivated organization (super admin only).
 *
 * @throws {Error} If not authorized or reactivation fails
 */
export async function reactivateOrganization(
  input: ReactivateOrganizationRequest
): Promise<ReactivateOrganizationResponse> {
  const { error } = await supabase.rpc('admin_set_organization_deactivated', {
    p_org_id: input.org_id,
    p_deactivated: false,
  })

  if (error) {
    throw new Error(`Failed to reactivate organization: ${error.message}`, {
      cause: error,
    })
  }

  return { success: true }
}

/**
 * Extracts the edge function's own error message from a FunctionsHttpError
 * (supabase-js otherwise surfaces a generic "non-2xx status code" message).
 */
async function edgeFunctionErrorMessage(
  error: unknown,
  fallback: string
): Promise<string> {
  if (error instanceof FunctionsHttpError) {
    try {
      const body = await error.context.json()
      if (typeof body?.error === 'string') {
        return body.error
      }
    } catch {
      // Body unreadable or not JSON — fall through
    }
  }
  return error instanceof Error ? error.message || fallback : fallback
}

/**
 * Request body for inviting a user to an organization
 */
export interface InviteUserRequest {
  email: string
  org_id: string
  role: OrganizationRole
  origin: string
  /** Super admin only — free-text cohort note stored on the invitation (e.g. "Centra") */
  note?: string
}

export type InviteStatus =
  'already_member' | 'invitation_resent' | 'invitation_created'

/**
 * Response from inviting a user
 */
export interface InviteUserResponse {
  email: string
  invite_status: InviteStatus
}

/**
 * Invites a user to an organization via email (super admin or org admin).
 *
 * Calls the invite-user edge function which:
 * - Verifies authorization SERVER-SIDE (super admin can invite to any org, org admins to their orgs)
 * - Creates an invitation record in organization_invitations table
 * - Sends an email (via Resend) linking to /auth/invitation?token=<secret>
 *
 * Flow after user clicks link:
 * 1. /auth/invitation calls verify-invitation. A first-time invitee is signed
 *    in by the link; anyone with an account signs in normally first
 * 2. User is redirected to /auth/accept-invite with org_id parameter
 * 3. accept_invitation creates the membership with the invited role
 * 4. Frontend checks profile.onboarding_completed_at to determine if password setup needed
 *
 * @throws {Error} If validation fails, not authorized, or invitation fails
 */
export async function inviteUser(
  input: InviteUserRequest
): Promise<InviteUserResponse> {
  const { data, error } = await supabase.functions.invoke('invite-user', {
    body: input,
  })

  if (error) {
    throw new Error(
      await edgeFunctionErrorMessage(error, 'Failed to invite user'),
      { cause: error }
    )
  }

  return data as InviteUserResponse
}

/**
 * A candidate invitee extracted by the bulk-invite-analyze edge function
 */
export interface AnalyzedInvitee {
  email: string
  name: string | null
  org_name: string | null
  warning: string | null
}

export interface AnalyzeInviteesResponse {
  invitees: AnalyzedInvitee[]
  source: 'llm' | 'fallback'
}

/**
 * Extracts invitee candidates from raw text (CSV or pasted list) via the
 * bulk-invite-analyze edge function (super admin only). Emails are verified
 * server-side to appear verbatim in the content — the LLM only enriches.
 *
 * @throws {Error} If validation fails or the caller is not a super admin
 */
export async function analyzeInvitees(input: {
  content: string
}): Promise<AnalyzeInviteesResponse> {
  const { data, error } = await supabase.functions.invoke(
    'bulk-invite-analyze',
    { body: input }
  )

  if (error) {
    throw new Error(
      await edgeFunctionErrorMessage(error, 'Failed to analyze invitees'),
      { cause: error }
    )
  }

  return data as AnalyzeInviteesResponse
}

/**
 * Request body for provisioning an organization (super admin only)
 */
// ── MCP OAuth Clients ─────────────────────────────────────────────────
// Platform-level catalog of pre-registered OAuth clients (one row per
// non-DCR provider, e.g. Slack). Read directly via RLS (super-admin
// SELECT policy on mcp_oauth_clients); writes go through the
// admin-mcp-oauth-client-* edge functions so the client_secret can be
// mediated through Vault. The client_secret value is never returned to
// the browser — only the public columns (client_id, scopes, etc.) are.

export interface AdminMcpOAuthClient {
  provider: string
  server_hostname: string
  display_name: string
  client_id: string
  scopes: string[]
  extra_authorize_params: Record<string, string>
  notes: string | null
  created_at: string
  updated_at: string
}

export const adminMcpOAuthClientsQueryOptions = () =>
  queryOptions({
    queryKey: ['admin', 'mcp-oauth-clients'],
    queryFn: async (): Promise<AdminMcpOAuthClient[]> => {
      const { data, error } = await supabase
        .from('mcp_oauth_clients')
        .select(
          'provider, server_hostname, display_name, client_id, scopes, extra_authorize_params, notes, created_at, updated_at'
        )
        .order('provider', { ascending: true })

      if (error) {
        throw new Error(`Failed to fetch MCP OAuth clients: ${error.message}`, {
          cause: error,
        })
      }
      return (data ?? []) as AdminMcpOAuthClient[]
    },
    staleTime: 60 * 1000,
  })

export interface UpsertMcpOAuthClientRequest {
  provider: string
  server_hostname?: string
  display_name: string
  client_id: string
  client_secret?: string
  scopes?: string[]
  extra_authorize_params?: Record<string, string>
  notes?: string
}

export async function upsertMcpOAuthClient(
  input: UpsertMcpOAuthClientRequest
): Promise<AdminMcpOAuthClient> {
  const { data, error } = await supabase.functions.invoke(
    'admin-mcp-oauth-client-upsert',
    { body: input }
  )
  if (error) {
    throw new Error(error.message || 'Failed to save OAuth client', {
      cause: error,
    })
  }
  return (data as { client: AdminMcpOAuthClient }).client
}

export async function deleteMcpOAuthClient(provider: string): Promise<void> {
  const { error } = await supabase.functions.invoke(
    'admin-mcp-oauth-client-delete',
    { body: { provider } }
  )
  if (error) {
    throw new Error(error.message || 'Failed to delete OAuth client', {
      cause: error,
    })
  }
}
